// Aufgeteilt aus dem früheren 2600-Zeilen-Monolithen `musicXmlDom.ts` (Import-Pfad unverändert:
// `@/composables/musicXmlDom` zeigt auf `index.ts`). Abhängigkeitsregel im Ordner: geteilte
// Basis-Helfer liegen in `core.ts`; gerichtete Geschwister-Imports sind erlaubt, ZYKLEN nicht
// (aktuell: bass→chordInference, notes→core, notes→bass, diatonic→notes, structure→notes,
// lyrics→bass, clipboard→structure, fragment→structure).
import {setBassLyric} from './bass';
import {type NoteInfo, chordGroupOf, findPartAndMeasureNote, parseMusicXml, serializeMusicXml, setPitch, voiceOf} from './core';

/** music21 nameWithOctave (Flat = '-', z.B. "B-3"/"F#4"/"C5") → step/alter/octave; `null` bei
 *  unparsebarer Eingabe. (Griffschrift-Staff-Tonhöhen sind im normalen Bereich, keine neg. Oktaven.) */
export function parseNameWithOctave(name: string): { step: string; alter: number; octave: number } | null {
    const m = /^([A-G])([#-]*)(-?\d+)$/.exec(name.trim());
    if (!m) return null;
    const step = m[1] ?? '';
    let alter = 0;
    for (const ch of m[2] ?? '') alter += ch === '#' ? 1 : -1;
    return {step, alter, octave: parseInt(m[3] ?? '', 10)};
}


/** Setzt/entfernt `<notehead>` (Form + Farbe) – schema-korrekt nach `type/dot/accidental/stem`, vor
 *  `staff/beam/notations/lyric`. Eine normale, farblose Note hat **kein** `<notehead>` (music21-
 *  Konvention), daher wird es dann entfernt. */
export function setNotehead(doc: Document, noteEl: Element, shape: string, color: string | null): void {
    const needed = (!!shape && shape !== 'normal') || !!color;
    let nh = noteEl.querySelector('notehead');
    if (!needed) {
        nh?.remove();
        return;
    }
    if (!nh) {
        nh = doc.createElement('notehead');
        const anchor = [...noteEl.children].find((c) =>
            ['staff', 'beam', 'notations', 'lyric'].includes(c.tagName),
        );
        if (anchor) noteEl.insertBefore(nh, anchor);
        else noteEl.appendChild(nh);
    }
    nh.textContent = shape || 'normal';
    nh.setAttribute('parentheses', 'no');
    if (color) nh.setAttribute('color', color);
    else nh.removeAttribute('color');
}

/** Schaltet den Druck-Unterstrich-Lyric (`number="10"`, Text „――") an/aus. */
export function setDruckUnderline(doc: Document, noteEl: Element, on: boolean): void {
    const existing = [...noteEl.querySelectorAll('lyric')].find(
        (l) => l.getAttribute('number') === '10',
    );
    if (!on) {
        existing?.remove();
        return;
    }
    if (existing) return;
    const lyric = doc.createElement('lyric');
    lyric.setAttribute('number', '10');
    lyric.setAttribute('name', '10');
    const syllabic = doc.createElement('syllabic');
    syllabic.textContent = 'single';
    const text = doc.createElement('text');
    text.textContent = '――'; // zwei U+2015 (HORIZONTAL BAR), wie der Konverter
    lyric.appendChild(syllabic);
    lyric.appendChild(text);
    noteEl.appendChild(lyric); // <lyric> steht schema-mäßig zuletzt
}

/** Die `<pedal>`-Elemente einer `<direction>` (Kind des Takts), sonst keine. */
function pedalsOf(child: Element): Element[] {
    return child.tagName.toLowerCase() === 'direction' ? [...child.querySelectorAll('pedal')] : [];
}

function samePedalNumber(a: Element, b: Element): boolean {
    return (a.getAttribute('number') ?? '1') === (b.getAttribute('number') ?? '1');
}

/**
 * Die Druck-Markierung des AUTO-Modus, die `group` umschließt: ein `<pedal type="start">` davor und
 * das passende `stop` (gleiche `number`) danach, **im selben Takt** – so schreibt die Engine sie
 * (`pipeline._create_merged_measure`, eine Spanne je Druck-Takt). Liefert die beiden `<direction>`-
 * Kinder des Takts samt Pedal; `null`, wenn die Note nicht in einer solchen Spanne liegt.
 * Takt-übergreifende Spannen (nur aus Fremd-Editoren) werden bewusst nicht angefasst.
 */
function enclosingDruckPedal(
    group: readonly Element[],
): { start: Element; startDirection: Element; stop: Element; stopDirection: Element } | null {
    const first = group[0];
    const last = group[group.length - 1];
    const measure = first?.parentElement;
    if (!first || !last || !measure) return null;
    const children = [...measure.children];
    const from = children.indexOf(first);
    const to = children.indexOf(last);
    let open: { start: Element; startDirection: Element } | null = null;
    for (const child of children.slice(0, from)) {
        for (const p of pedalsOf(child)) {
            const type = p.getAttribute('type');
            if (type === 'start') open = {start: p, startDirection: child};
            else if (type === 'stop' && open && samePedalNumber(open.start, p)) open = null;
        }
    }
    if (!open) return null;
    for (const child of children.slice(to + 1)) {
        const stop = pedalsOf(child).find(p => p.getAttribute('type') === 'stop' && samePedalNumber(open.start, p));
        if (stop) return {...open, stop, stopDirection: child};
    }
    return null;
}

/** Entfernt ein `<pedal>` samt leer gewordenem `<direction-type>`/`<direction>`. */
function removePedal(pedal: Element, direction: Element): void {
    const directionType = pedal.parentElement;
    pedal.remove();
    if (directionType && directionType.children.length === 0) directionType.remove();
    if (direction.querySelectorAll('direction-type').length === 0) direction.remove();
}

/**
 * Setzt die Balgrichtung EINER getauschten Note (`group` = ihre Akkordgruppe) in der Griffschrift-
 * Ausgabe. Es gibt zwei Druck-Signale, und beide liest `griffschrift/mapping/bellows.py::BellowsReader`:
 * den Druck-Unterstrich (`number="10"`, GLOBAL-Modus) an der Note und die `<pedal>`-Spanne über dem
 * ganzen Takt (AUTO-/Per-Takt-Modus). Zug ist das Fehlen beider.
 *
 * **Druck → Zug in einer Pedal-Spanne löst die Spanne auf (kritisch – nicht auf das bloße Entfernen
 * des Unterstrichs rückbauen).** Sonst bliebe die Note überspannt, jede folgende
 * `POST /api/note-grips`-Abfrage läse sie weiter als Druck und bildete ihre (jetzt Zug-)Staff-
 * Position auf den falschen Knopf ab. Die übrigen Noten der Spanne in derselben Stimme behalten Druck
 * über den Unterstrich – genau das Signal des GLOBAL-Modus; Pausen, Akkord-Nebentöne und die
 * Bass-Träger anderer Stimmen bekommen keinen. Ein Druck-Griff innerhalb einer Spanne braucht
 * dagegen keinen Unterstrich (er stünde doppelt).
 */
function setBellowsDirection(doc: Document, group: readonly Element[], isDruck: boolean): void {
    const primary = group[0];
    if (!primary) return;
    const span = enclosingDruckPedal(group);
    if (!span) {
        setDruckUnderline(doc, primary, isDruck);
        return;
    }
    if (isDruck) return;
    setDruckUnderline(doc, primary, false);
    const measure = primary.parentElement!;
    const children = [...measure.children];
    const inSpan = children.slice(children.indexOf(span.startDirection) + 1, children.indexOf(span.stopDirection));
    const voice = voiceOf(primary);
    for (const el of inSpan) {
        if (el.tagName.toLowerCase() !== 'note' || group.includes(el)) continue;
        if (voiceOf(el) !== voice || el.querySelector('rest') || el.querySelector('chord')) continue;
        if (el.getAttribute('print-object') === 'no') continue;
        setDruckUnderline(doc, el, true);
    }
    removePedal(span.start, span.startDirection);
    removePedal(span.stop, span.stopDirection);
}

// ============ Bindebögen (Tie / Slur) ============

/** Tonhöhen-Schlüssel (step:alter:octave) für den Tie/Slur-Vergleich; leer bei Pausen. */
export function pitchKey(noteEl: Element): string {
    const p = noteEl.querySelector('pitch');
    if (!p) return '';
    const step = p.querySelector('step')?.textContent || '';
    const alter = p.querySelector('alter')?.textContent || '0';
    const octave = p.querySelector('octave')?.textContent || '';
    return `${step}:${alter}:${octave}`;
}

/** Nächste spielbare Note (gleiche Stimme, keine Pause, kein Chord-Begleiter) im selben Part –
 *  über Taktgrenzen hinweg. */
function findNextPlayableNoteElement(part: Element, aEl: Element, voice: number): Element | null {
    const all = [...part.querySelectorAll('note')];
    const aIdx = all.indexOf(aEl);
    if (aIdx < 0) return null;
    // Ab der Position NACH aEl suchen – funktioniert damit auch, wenn aEl selbst ein
    // `<chord/>`-Begleiter ist (z. B. beim Entfernen eines Akkordtons mit ausgehendem Bogen).
    return all.slice(aIdx + 1).find(
        n => voiceOf(n) === voice && !n.querySelector('chord') && !n.querySelector('rest'),
    ) ?? null;
}

function getOrCreateNotations(doc: Document, noteEl: Element): Element {
    let notations = noteEl.querySelector('notations');
    if (!notations) {
        notations = doc.createElement('notations');
        const firstLyric = noteEl.querySelector('lyric');
        if (firstLyric) noteEl.insertBefore(notations, firstLyric);
        else noteEl.appendChild(notations);
    }
    return notations;
}

export function insertTieElement(doc: Document, noteEl: Element, type: 'start' | 'stop'): void {
    const tie = doc.createElement('tie');
    tie.setAttribute('type', type);
    const dur = noteEl.querySelector('duration'); // MusicXML: <tie> direkt nach <duration>
    if (dur?.nextSibling) noteEl.insertBefore(tie, dur.nextSibling);
    else noteEl.appendChild(tie);
}

export function addTiedNotation(doc: Document, noteEl: Element, type: 'start' | 'stop'): void {
    const notations = getOrCreateNotations(doc, noteEl);
    const tied = doc.createElement('tied');
    tied.setAttribute('type', type);
    notations.insertBefore(tied, notations.firstChild);
}

export function hasTie(noteEl: Element, type: 'start' | 'stop'): boolean {
    return [...noteEl.querySelectorAll('tie')].some(t => t.getAttribute('type') === type);
}

export function removeTieEnds(noteEl: Element, type: 'start' | 'stop'): void {
    for (const t of [...noteEl.querySelectorAll('tie')]) {
        if (t.getAttribute('type') === type) t.remove();
    }
    const notations = noteEl.querySelector('notations');
    if (notations) {
        for (const t of [...notations.querySelectorAll('tied')]) {
            if (t.getAttribute('type') === type) t.remove();
        }
        if (!notations.children.length) notations.remove();
    }
}

function slurNumbers(noteEl: Element, type: 'start' | 'stop'): number[] {
    return [...noteEl.querySelectorAll('slur')]
        .filter(s => s.getAttribute('type') === type)
        .map(s => parseInt(s.getAttribute('number') || '1', 10));
}

function addSlurEnd(doc: Document, noteEl: Element, type: 'start' | 'stop', n: number): void {
    const notations = getOrCreateNotations(doc, noteEl);
    const slur = doc.createElement('slur');
    slur.setAttribute('type', type);
    slur.setAttribute('number', n.toString());
    notations.appendChild(slur);
}

function removeSlurEnd(noteEl: Element, type: 'start' | 'stop', n: number): void {
    const notations = noteEl.querySelector('notations');
    if (!notations) return;
    for (const s of [...notations.querySelectorAll('slur')]) {
        if (s.getAttribute('type') === type && parseInt(s.getAttribute('number') || '1', 10) === n) s.remove();
    }
    if (!notations.children.length) notations.remove();
}

/** Entfernt **alle ausgehenden** Bögen der Note `aEl` (jeder `<tie type="start">` und jeder
 *  `<slur type="start">`) samt der zugehörigen `stop`-Gegenstücke auf den Ziel-Noten. **Eingehende**
 *  Bögen (`stop` von der vorigen Note) bleiben unberührt. */
export function removeOutgoingBows(part: Element, aEl: Element, voice: number): void {
    // Ausgehender Tie (Schema: max. einer): Gegenstück = tie-stop der nächsten spielbaren Note.
    if (hasTie(aEl, 'start')) {
        const next = findNextPlayableNoteElement(part, aEl, voice);
        if (next) removeTieEnds(next, 'stop');
        removeTieEnds(aEl, 'start');
    }
    // Ausgehende Slurs (evtl. mehrere Nummern): je Nummer das slur-stop-Gegenstück NACH aEl suchen.
    const notes = [...part.querySelectorAll('note')];
    const aIdx = notes.indexOf(aEl);
    for (const n of slurNumbers(aEl, 'start')) {
        const stopNote = notes.slice(aIdx + 1).find(note => slurNumbers(note, 'stop').includes(n));
        if (stopNote) removeSlurEnd(stopNote, 'stop', n);
        removeSlurEnd(aEl, 'start', n);
    }
}

/** Gegenstück zu {@link removeOutgoingBows}: löst die **eingehenden** Bögen der Note `bEl` – den
 *  `tie-start` der vorigen Note gleicher Tonhöhe derselben Stimme und je `slur-stop`-Nummer den
 *  passenden `slur-start` davor –, samt der eigenen `stop`-Enden. Für das Löschen: bliebe der Start
 *  stehen, bände er nach dem Nachrücken die nächste aufrückende Note an (gleiche Tonhöhe → die
 *  beiden verschmölzen klingend) bzw. zeigte ins Leere. */
export function removeIncomingBows(part: Element, bEl: Element, voice: number): void {
    const notes = [...part.querySelectorAll('note')];
    const before = notes.slice(0, Math.max(0, notes.indexOf(bEl))).reverse();
    if (hasTie(bEl, 'stop')) {
        const key = pitchKey(bEl);
        const prev = before.find(n => voiceOf(n) === voice && !n.querySelector('grace') && pitchKey(n) === key);
        if (prev && hasTie(prev, 'start')) removeTieEnds(prev, 'start');
        removeTieEnds(bEl, 'stop');
    }
    for (const n of slurNumbers(bEl, 'stop')) {
        const startNote = before.find(note => slurNumbers(note, 'start').includes(n));
        if (startNote) removeSlurEnd(startNote, 'start', n);
        removeSlurEnd(bEl, 'stop', n);
    }
}

/**
 * Toggelt einen Bindebogen. **Hinzufügen** (Note hat keinen ausgehenden Bogen): zwischen der Note und
 * der direkt folgenden spielbaren Note – gleiche Tonhöhe → **Tie** (`<tie>` + `<tied>`), sonst →
 * **Slur** (`<slur>`). **Entfernen** (Note hat ausgehende Bögen): löst **alle** ausgehenden Bögen der
 * Note (Tie **und** Slur, inkl. `stop`-Gegenstücke) – auch bei Ketten, mehreren Slurs oder wenn der
 * Bogen zu einer nicht direkt folgenden Note geht; eingehende Bögen bleiben. Gibt `null` zurück, wenn
 * die Auswahl eine Pause ist oder (beim Hinzufügen) keine folgende spielbare Note existiert.
 */
export function toggleTieOrSlurInMusicXml(musicXml: string, noteInfo: NoteInfo): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const found = findPartAndMeasureNote(doc, noteInfo);
        if (!found) return null;
        const {part, note: aEl} = found;
        if (aEl.querySelector('rest')) return null; // kein Bogen von einer Pause

        // ENTFERNEN: sobald aEl irgendeinen ausgehenden Bogen hat → alle lösen (bEl nicht nötig).
        if (hasTie(aEl, 'start') || slurNumbers(aEl, 'start').length) {
            removeOutgoingBows(part, aEl, noteInfo.voice);
            return serializeMusicXml(doc);
        }

        // HINZUFÜGEN: braucht die nächste spielbare Note.
        const bEl = findNextPlayableNoteElement(part, aEl, noteInfo.voice);
        if (!bEl) return null;
        const keyA = pitchKey(aEl);
        const samePitch = keyA !== '' && keyA === pitchKey(bEl);
        if (samePitch) {
            insertTieElement(doc, aEl, 'start');
            addTiedNotation(doc, aEl, 'start');
            insertTieElement(doc, bEl, 'stop');
            addTiedNotation(doc, bEl, 'stop');
        } else {
            const used = [
                ...slurNumbers(aEl, 'start'), ...slurNumbers(aEl, 'stop'),
                ...slurNumbers(bEl, 'start'), ...slurNumbers(bEl, 'stop'),
            ];
            const n = used.length ? Math.max(...used) + 1 : 1;
            addSlurEnd(doc, aEl, 'start', n);
            addSlurEnd(doc, bEl, 'stop', n);
        }

        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error toggling tie/slur in MusicXML:', e);
        return null;
    }
}

/** Minimal-Kontrakt eines gewählten Griff-Knopfes (strukturell kompatibel zu `GripButton`). */
interface GripButtonLike {
    griffschrift_pitch: string;
    notehead: string;
    color?: string | null;
}

/**
 * Tauscht **client-seitig** den Griff EINER Note der Griffschrift-**Ausgabe**: schreibt je Knopf die
 * angezeigte Staff-Tonhöhe (`griffschrift_pitch`), den Notenkopf (`notehead`) und die Farbe (auf
 * `<note>` UND `<notehead>`) und setzt die Balgrichtung (`isDruck`, siehe `setBellowsDirection`).
 * Wechselt die Richtung, wechselt auch die Bass-Silbe unter der Note: `bassLyric` ist das
 * `Grip.bass_lyric` der note-grips-Antwort (`null` = stehen lassen, `''` = entfernen). Nur
 * Tonhöhe/Notenkopf/Farbe/Lyric/Druck-Pedal ändern sich – **nie `<duration>`** –, daher bleibt die
 * Taktlänge gültig (keine `MeterException`). Die klingende Tonhöhe ändert sich nicht. Akkorde werden
 * über ihre ganze `<note>`-Gruppe getauscht (Tonzahl ist über Alternativen konstant).
 * `null` bei Fehler oder Tonzahl-Mismatch. Datenquelle sind die `Grip`s der note-grips-Antwort.
 */
export function applyGripToNoteInMusicXml(
    musicXml: string,
    noteInfo: NoteInfo,
    buttons: readonly GripButtonLike[],
    isDruck: boolean,
    bassLyric: string | null = null,
): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const found = findPartAndMeasureNote(doc, noteInfo);
        if (!found) return null;
        const group = chordGroupOf(found.note);
        if (group.length === 0 || group.length !== buttons.length) return null; // Tonzahl muss passen
        group.forEach((noteEl, i) => {
            const b = buttons[i];
            if (!b) return;
            const color = b.color && b.color.trim() ? b.color : null;
            const pitch = parseNameWithOctave(b.griffschrift_pitch);
            if (pitch) setPitch(doc, noteEl, pitch.step, pitch.alter, pitch.octave);
            setNotehead(doc, noteEl, b.notehead, color);
            if (color) noteEl.setAttribute('color', color);
            else noteEl.removeAttribute('color');
        });
        setBellowsDirection(doc, group, isDruck);
        const primary = group[0];
        if (primary && bassLyric !== null) setBassLyric(doc, primary, bassLyric);
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error applying grip to note in MusicXML:', e);
        return null;
    }
}
